/* eslint-disable no-use-before-define */
/* eslint-disable simple-import-sort/imports */
import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@material-ui/core';
import { ChevronRight, CheckCircle } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { appLanguage } from '@orientaction/reducers';
import { useSelector } from 'react-redux';
import { getFromLS } from '@orientaction/utils';
import { getMe, sendMailForMore } from '../../services/user.service';
import { useHistory } from 'react-router-dom';
import { kebabCase } from 'lodash';
import SimpleModal from '../SimpleModal';

const useStyles = makeStyles(theme => ({
  card: {
    background: 'radial-gradient(circle at bottom right, #4A90E2 0%, #000A8C 70%)',
    borderRadius: 10,
    position: 'relative',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    minHeight: 293,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: 'none',
    boxShadow: '0px 4px 12px rgba(0, 10, 140, 0.2)',
    padding: 0,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0px 8px 20px rgba(0, 10, 140, 0.3)',
    },
  },
  imageContainer: {
    width: 'calc(100% - 32px)',
    // height: 232,
    borderRadius: 0,
    overflow: 'hidden',
    position: 'absolute',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    aspectRatio: '1/1',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  content: {
    padding: '0 !important',
    paddingTop: 0,
    paddingBottom: '0 !important',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    '&:last-child': {
      paddingBottom: '0 !important',
    },
  },
  titleColumn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingRight: theme.spacing(1),
  },
  title: {
    color: 'white',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
    margin: 0,
  },
  iconColumn: {
    width: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    width: 'calc(100% - 32px)',
    height: 72,
    position: 'absolute',
    top: 218,
    left: '50%',
    transform: 'translateX(-50%)',
    [theme.breakpoints.down('sm')]: {
      height: 73,
      top: 215,
    },
  },
  chevron: {
    color: 'white',
    fontSize: 20,
  },
  testDoneContainer: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#E74242',
    borderRadius: 0,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    zIndex: 2,
    width: 74,
    height: 22,
  },
  testDoneText: {
    color: 'white',
    fontSize: 10,
    fontWeight: 600,
  },
  checkIcon: {
    color: 'white',
    fontSize: 10,
  },
  bodycenter: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    zIndex: 8,
  },
}));

interface IRecommendedTestCardProps {
  test: any;
  isTestDone?: boolean;
  onClick?: () => void;
  me?: any;
  freeTests?: any;
}

const RecommendedTestCard: React.FC<IRecommendedTestCardProps> = ({
  test,
  isTestDone = false,
  onClick,
  me,
  freeTests,
}) => {
  const classes = useStyles();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const { push } = useHistory();

  // URL par défaut pour l'image si pas de thumbnail
  const defaultImageUrl = '/images/MajorTest/preferences-jeunes.png';

  // Priorité : thumbnail -> image -> défaut
  const imageUrl =
    test?.attributes?.thumbnail ||
    test?.thumbnail ||
    test?.attributes?.image ||
    test?.image ||
    defaultImageUrl;

  const [loadingMe2, setLoadingMe2] = useState<boolean>(false);
  const [messageData, setMessageData] = useState<string>(
    'Vous avez atteint la limite de tests offerts gratuitement par ORIENTACTION'
  );
  const [checkingSubscription, setCheckingSubscription] = useState<boolean>(false);

  const paymentLink = process.env.REACT_APP_PAYMENT_LINK;
  const paymentLinkNoRattached = process.env.REACT_APP_PAYMENT_LINK_NO_RATTACHED;
  const lang: string = useSelector(appLanguage);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const token = getFromLS('userToken') || '';

  const redirect = async () => {
    setLoadingMe2(true);
    const dataMe = await getMe(token);
    const minimum = me.beneficiaryType === 'rattached' ? 10 : 3;
    const tests_to_do = dataMe.data.tests_to_do?.map((el: any) => el.id);
    // const minimum = 1;
    if (
      freeTests.includes(test.id) ||
      dataMe.data.counterTest === null ||
      dataMe.data.counterTest < minimum ||
      dataMe.data.statusSubscription === 'active' ||
      tests_to_do.includes(test.id)
    ) {
      push(`/test/${test?.id}/${kebabCase(test?.attributes?.title)}`);
    } else {
      setOpenModal(true);
    }
    setLoadingMe2(false);
  };

  const sendMail = async () => {
    const resp = await sendMailForMore(me.id, token);
    if (resp) {
      setOpenModal(false);
    }
  };

  const paySubscription = () => {
    window.open(
      `${
        me.beneficiaryType === 'rattached' ? paymentLink : paymentLinkNoRattached
      }?prefilled_email=${me.email}&locale=${lang === 'GB' ? 'eng' : 'fr'}&client_reference_id=${
        me.id
      }`,
      '_blank'
    );
    setOpenModal(false);
  };

  return (
    <Card className={classes.card} onClick={redirect}>
      {isTestDone && (
        <Box className={classes.testDoneContainer}>
          <CheckCircle className={classes.checkIcon} />
          <Typography className={classes.testDoneText}>Test fait</Typography>
        </Box>
      )}

      {loadingMe2 && (
        <div className={classes.bodycenter}>
          <CircularProgress color="primary" />
        </div>
      )}

      <div className={classes.imageContainer}>
        <img
          src={imageUrl}
          alt={test?.attributes?.title || 'Test'}
          className={classes.image}
          onError={e => {
            // Fallback en cas d'erreur de chargement de l'image
            (e.target as HTMLImageElement).src = '/images/MajorTest/preferences-jeunes.png';
          }}
        />
      </div>
      <div className={classes.footer}>
        <div className={classes.titleColumn}>
          <Typography className={classes.title}>
            {test?.attributes?.title || test?.title || 'Test sans titre'}
          </Typography>
        </div>
        <div className={classes.iconColumn}>
          <ChevronRight className={classes.chevron} />
        </div>
      </div>
      <SimpleModal
        open={openModal}
        handleClose={sendMail}
        handleClick={paySubscription}
        title="Information"
        message={messageData}
        textClose="Non merci"
        textClick="Débloquer"
      />
    </Card>
  );
};

export default RecommendedTestCard;
